/**
 * Discover Contract Types
 * Single source of truth for discovery filtering and results
 */

/** Sort modes for discovery results */
export type SortMode = 'relevance' | 'latest' | 'price_asc' | 'price_desc';

/** Filter state for discovery queries */
export interface DiscoverFilters {
  /** Full-text search query */
  q: string;
  /** Subject/topic filters (array overlap) */
  subjects: string[];
  /** Geographic filters (array overlap) */
  geo: string[];
  /** Brand category filters (array overlap) */
  brand_categories: string[];
  /** Psychographic filters (array overlap) */
  psychographics: string[];
  /** Brand geographic focus filters (array overlap) */
  brand_geographic_focus: string[];
  /** Sort mode */
  sort: SortMode;
  /** Page number (1-indexed) */
  page: number;
  /** Page size */
  pageSize: number;
  
  // V3 Taxonomy filters (optional for backwards compatibility)
  /** Industry IDs filter (normalized taxonomy) */
  industry_ids?: string[];
  /** Category IDs filter (normalized taxonomy) */
  category_ids?: string[];
  /** Subcategory IDs filter (normalized taxonomy) */
  subcategory_ids?: string[];
  /** Hashtag IDs filter */
  hashtag_ids?: string[];
  // Range filters (dollars)
  /** Minimum price filter ($) */
  price_min?: number;
  /** Maximum price filter ($) */
  price_max?: number;
  /** Minimum discount filter ($) */
  discount_min?: number;
  /** Maximum discount filter ($) */
  discount_max?: number;
  /** Minimum commission filter ($) */
  commission_min?: number;
  /** Maximum commission filter ($) */
  commission_max?: number;
}

/** Single facet option with count */
export interface FacetOption {
  value: string;
  count: number;
}

/** Taxonomy facet option with ID */
export interface TaxonomyFacetOption {
  id: string;
  label: string;
  count: number;
}

/** Hashtag facet option */
export interface HashtagFacetOption {
  id: string;
  tag: string;
  count: number;
}

/** Response from facets RPC (v2) */
export interface FacetResponse {
  subjects: FacetOption[];
  geo: FacetOption[];
  brand_categories: FacetOption[];
  psychographics: FacetOption[];
  brand_geographic_focus: FacetOption[];
  total: number;
}

/** Response from facets RPC (v3) - includes taxonomy facets and hashtags */
export interface FacetResponseV3 extends FacetResponse {
  /** Industries (always returned) */
  industries: TaxonomyFacetOption[];
  /** Categories (only when industry selected) */
  categories: TaxonomyFacetOption[];
  /** Subcategories (only when category selected) */
  subcategories: TaxonomyFacetOption[];
  /** Hashtags (top 20 by deal count) */
  hashtags: HashtagFacetOption[];
}

/** Demographics filter structure from deals.demo_filters */
export interface DemoFilters {
  age_min?: number;
  age_max?: number;
  genders?: string[];
}

/** Taxonomy item from v3 view */
export interface TaxonomyItem {
  id: string;
  slug: string;
  label: string;
}

/** Category taxonomy item with industry reference */
export interface CategoryItem extends TaxonomyItem {
  industry_id: string;
}

/** Subcategory taxonomy item with category reference */
export interface SubcategoryItem extends TaxonomyItem {
  category_id: string;
}

/** Custom subcategory item (brand-scoped) */
export interface CustomSubcategoryItem {
  id: string;
  slug: string;
  label: string;
  industry_id: string;
  category_id: string;
}

/** Hashtag item */
export interface HashtagItem {
  id: string;
  tag: string;
}

/**
 * DiscoverDeal - Row shape from discover_deals_v2 view
 * Represents a single discoverable deal with all taxonomy and product data
 */
export interface DiscoverDeal {
  // Deal core
  deal_id: string;
  brand_id: string;
  title: string;
  description: string | null;
  status: string;
  starts_at: string | null;
  expires_at: string | null;
  created_at: string | null;
  
  // Deal pricing/commission (legacy)
  commission_rate: number;
  discount_value: number;
  
  // Canonical cents pricing (Phase 16C)
  sale_cents: number | null;
  msrp_cents: number | null;
  creator_commission_pct: number | null;
  friend_discount_pct: number | null;
  platform_fee_pct: number | null;
  creator_bonus_cents: number | null;
  currency: string | null;
  store_product_id: string | null;
  
  // Deal media
  cover_image_url: string | null;
  video_url: string | null;
  hero_image_variant: string | null;
  
  // Taxonomy signals (from deals)
  geo_filters: string[] | null;
  subject_filters: string[] | null;
  demo_filters: DemoFilters | null;
  
  // Brand info
  brand_name: string;
  brand_avatar_url: string | null;
  
  // Brand taxonomy (from brand_assessments)
  brand_categories: string[] | null;
  brand_psychographics: string[] | null;
  brand_geographic_focus: string[] | null;
  
  // Product summary (aggregated)
  primary_product_id: string | null;
  price_min: number | null;
  price_max: number | null;
  commission_percent: number | null;
  discount_percent: number | null;
  image_url: string | null;
  product_count: number;
  
  // Adoption stats
  adopt_count: number;
  latest_adopted_at: string | null;
}

/**
 * DiscoverDealV3 - Row shape from discover_deals_v3 view
 * Extends v2 with normalized taxonomy data, custom subcategories, and hashtags
 */
export interface DiscoverDealV3 extends DiscoverDeal {
  // Brand handle for public navigation
  brand_handle: string | null;
  // Normalized taxonomy (aggregated JSONB arrays)
  industries: TaxonomyItem[];
  categories: CategoryItem[];
  subcategories: SubcategoryItem[];
  // Brand-scoped custom subcategories (deal-specific only)
  custom_subcategories: CustomSubcategoryItem[];
  // Hashtags (deal-specific)
  hashtags: HashtagItem[];
}

/** Result from getDiscoverResults */
export interface DiscoverResultsResponse {
  items: DiscoverDeal[];
  total: number;
}

/** Result from getDiscoverResultsV3 */
export interface DiscoverResultsResponseV3 {
  items: DiscoverDealV3[];
  total: number;
}

/** Taxonomy coverage stats with dynamic money bounds */
export interface TaxonomyCoverage {
  total_deals: number;
  deals_with_taxonomy: number;
  deals_without_taxonomy: number;
  /** null = unknown (RPC error), number = actual coverage */
  coverage_percent: number | null;
  /** Count of deals matching current scope (same as total_deals when unscoped) */
  deal_count: number;
  /** Min/max price amounts from deals in scope (null if no deals) */
  min_price_amount: number | null;
  max_price_amount: number | null;
  /** Min/max discount amounts from deals in scope (null if no deals) */
  min_discount_amount: number | null;
  max_discount_amount: number | null;
  /** Min/max commission amounts from deals in scope (null if no deals) */
  min_commission_amount: number | null;
  max_commission_amount: number | null;
}

/** Parameters for scoped coverage query */
export interface TaxonomyCoverageParams {
  industry_ids?: string[];
  category_ids?: string[];
  subcategory_ids?: string[];
  price_min?: number;
  price_max?: number;
  discount_min?: number;
  discount_max?: number;
  commission_min?: number;
  commission_max?: number;
}

/** Default filter values */
export const DEFAULT_FILTERS: DiscoverFilters = {
  q: '',
  subjects: [],
  geo: [],
  brand_categories: [],
  psychographics: [],
  brand_geographic_focus: [],
  sort: 'relevance',
  page: 1,
  pageSize: 24,
  // V3 defaults
  industry_ids: [],
  category_ids: [],
  subcategory_ids: [],
  hashtag_ids: [],
};

// ============================================================
// Deal Pipeline View Models
// Single source of truth for deal → order → split types
// ============================================================

/** Media reference for deal gallery */
export interface DealMediaItem {
  url: string;
  type: 'image' | 'video';
  alt?: string;
  isPrimary?: boolean;
}

/** Core deal fields shared across all views */
export interface DealCore {
  id: string;
  brandId: string;
  title: string;
  description: string | null;
  heroImageUrl: string | null;
  media: DealMediaItem[];
  status: 'draft' | 'active' | 'paused';
  /** Taxonomy references */
  industryId: string | null;
  categoryId: string | null;
  subcategoryId: string | null;
  /** Product/store reference */
  productUrl: string | null;
  storeProductId: string | null;
  startsAt: string | null;
  endsAt: string | null;
}

/**
 * Canonical pricing inputs (brand-entered, stored in deals table).
 * All monetary values are integer cents.
 */
export interface DealPricingInputs {
  currency: string;
  msrpCents: number;
  saleCents: number;
  friendDiscountPct: number | null;
  friendDiscountCents: number | null;
  creatorCommissionPct: number | null;
  creatorBonusCents: number | null;
  platformFeePct: number | null;
}

/**
 * Creator-facing pricing view model.
 * Returned by compute_deal_pricing_view(role='creator').
 * Contains earnings/commission fields.
 */
export interface DealPricingViewCreator {
  currency: string;
  msrpCents: number;
  saleCents: number;
  discountLabel: string | null;
  friendDiscountPct: number | null;
  friendPriceCents: number | null;
  creatorCommissionPct: number | null;
  creatorBonusCents: number | null;
  creatorEarningsEstimateCents: number | null;
}

/**
 * Customer-facing pricing view model.
 * Returned by compute_deal_pricing_view(role='customer').
 * MUST NOT contain earnings, commission, or payout fields.
 */
export interface DealPricingViewCustomer {
  currency: string;
  msrpCents: number;
  saleCents: number;
  discountLabel: string | null;
  friendDiscountPct: number | null;
  friendPriceCents: number | null;
}

/** Role-aware pricing — discriminated by role tag */
export type DealPricingView =
  | ({ role: 'creator' } & DealPricingViewCreator)
  | ({ role: 'customer' } & DealPricingViewCustomer);

/** Card-level view model consumed by UniversalDealCard */
export interface DealCardVM {
  /** Deal core display fields */
  id: string;
  title: string;
  description: string | null;
  heroImageUrl: string | null;
  brandName: string;
  brandLogoUrl: string | null;
  status: 'draft' | 'active' | 'paused';
  category: string | null;
  tags: string[];
  productCount: number;
  /** Role-aware pricing block */
  pricing: DealPricingView;
  /** Card badges / labels */
  isNew?: boolean;
  isExclusive?: boolean;
  /** Creator-only: selection state */
  isSelected?: boolean;
  selectedAt?: string | null;
}

/** Detail-level view model for full deal page */
export interface DealDetailVM extends DealCardVM {
  /** Extended content */
  longDescription: string | null;
  media: DealMediaItem[];
  /** CTA state */
  canBuy: boolean;
  canAddToCart: boolean;
  canAddToWishlist: boolean;
  /** Purchase availability */
  inStock: boolean;
  startsAt: string | null;
  endsAt: string | null;
  /** Store/product info */
  productUrl: string | null;
  storePlatform: string | null;
  shipping: string | null;
}

/** Checkout preview / order draft */
export interface OrderDraftVM {
  dealId: string;
  qty: number;
  creatorUserId: string | null;
  inviteToken: string | null;
  /** Preview amounts (cents) */
  subtotalCents: number;
  taxCents: number;
  shippingCents: number;
  totalCents: number;
  currency: string;
}

/** Single row in the payout split ledger */
export interface SplitLedgerEntry {
  id: string;
  orderId: string;
  recipientType: 'brand' | 'creator' | 'platform';
  recipientId: string;
  amountCents: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed';
}

/** Viewer role for deal pipeline RPCs */
export type DealViewerRole = 'creator' | 'customer' | 'brand';
